import { STATUS_MAP } from '../../utils/catalogs';
import { TOAST_ACTION, TOAST_TONE } from '../feedback/feedbackConstants';

/** The handle of the history's error toasts, whichever action of it failed. */
export const DELIVERIES_ACTION_ERROR = 'deliveries-action-error';

/** "1 envío" / "3 envíos". */
const amount = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * The outcome of a bulk change. All of it changed: good news that goes away. A
 * part did not: a warning that stays, because the selection is cleared and the
 * toast is the only record of it. The backend does not say why each one was
 * left alone, so `reason` names every filter it applies.
 */
const bulkOutcome = ({ requested, updated }, done, reason) => {
  const skipped = requested - updated;
  if (skipped <= 0) return `${done}.`;
  return {
    tone: TOAST_TONE.WARNING,
    sticky: true,
    message: `${done}. ${amount(skipped, 'no cambió', 'no cambiaron')}: ${reason}.`,
  };
};

/**
 * What a label download produced. Partial is a warning that stays: the
 * operator has to know which bultos did not make it into the PDF.
 */
const labelsOutcome = ({ url, opened, bultos, included, withoutLabel, unreadable }) => {
  const parts = [`PDF listo: ${included} de ${amount(bultos, 'etiqueta', 'etiquetas')}.`];
  if (withoutLabel > 0) {
    parts.push(`${amount(withoutLabel, 'bulto no tiene', 'bultos no tienen')} etiqueta todavía.`);
  }
  if (unreadable.length > 0) parts.push(`No se pudieron leer: ${unreadable.join(', ')}.`);

  const partial = withoutLabel > 0 || unreadable.length > 0;
  return {
    tone: partial ? TOAST_TONE.WARNING : TOAST_TONE.SUCCESS,
    message: parts.join(' '),
    // The browser blocked the tab: this button opens it inside a click.
    action: opened ? null : { label: 'Abrir PDF', type: TOAST_ACTION.OPEN_URL, url },
    sticky: partial || !opened,
  };
};

const LABELS_RULE = {
  pending: 'Armando el PDF de etiquetas...',
  success: ({ payload }) => labelsOutcome(payload),
  testId: 'deliveries-labels-feedback',
  errorTestId: DELIVERIES_ACTION_ERROR,
};

/**
 * The toasts of the bulk actions of the deliveries history and of the order
 * upload. See `feedbackCatalog` for what each field means.
 */
export const DELIVERIES_FEEDBACK_RULES = {
  // The manual form sends the user to the history the moment the order is in,
  // and a toast outlives the page that earned it. Errors stay in the form,
  // next to what was typed.
  'deliveries/createSingleOrder': {
    // `create_single_order/` answers "order created successfully", which is
    // internal English, so the wording is the front's own here.
    success: 'Orden creada con éxito.',
    error: 'none',
    testId: 'upload-notice',
  },

  // A refused spreadsheet is the panel's to show, or the job's toast once the
  // user has left it.
  'deliveries/uploadOrdersExcel': {
    // The count comes from the answer: the front knows how many rows it sent,
    // not how many orders the backend ended up creating.
    success: ({ payload }) => payload?.message || 'Órdenes creadas con éxito.',
    error: 'none',
    testId: 'upload-notice',
  },

  'deliveries/fulfillDeliveries': {
    success: ({ payload }) =>
      bulkOutcome(
        payload,
        amount(payload.updated, 'envío preparado', 'envíos preparados'),
        'ya iban en camino, estaban finalizados o no son de tu operación'
      ),
    testId: 'deliveries-action-success',
    errorTestId: DELIVERIES_ACTION_ERROR,
  },

  // Errors are shown inside the dialog, which stays open to retry.
  'deliveries/massiveChangeStatus': {
    success: ({ payload }) =>
      bulkOutcome(
        payload,
        `${amount(payload.updated, 'envío quedó', 'envíos quedaron')} en «${STATUS_MAP[payload.status]?.user_name || payload.status}»`,
        'estaban finalizados o no son de tu operación'
      ),
    error: 'none',
    testId: 'deliveries-action-success',
  },

  'deliveries/changeDriver': {
    success: ({ payload }) =>
      bulkOutcome(
        payload,
        `${amount(payload.updated, 'envío asignado', 'envíos asignados')}${payload.driverName ? ` a ${payload.driverName}` : ''}`,
        'estaban finalizados o son de otro operador'
      ),
    error: 'none',
    testId: 'deliveries-action-success',
  },

  'deliveries/downloadSelectedLabels': LABELS_RULE,
  'deliveries/downloadAllLabels': LABELS_RULE,
};
